/* eslint-disable react/jsx-no-target-blank */
import React from 'react';
import { UitkRating } from 'uitk-react-rating';
import gaLogger from 'src/utils/ga-logger';
import { getParameterByName, getLink, checkValue, onImageLoad as imageLoad, addExtraParamToUrl } from '../../../common/helper';
import { Typo } from 'src/components/common/ui';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';

const onClickGaTracking = (ga) => {
  const { eventCategory, eventAction, eventLabel } = ga;
  gaLogger.logGa(eventCategory, eventAction, eventLabel);
};

/**
 *
 * @param link
 * @param text
 * @param css
 * @param target
 * @returns
 */
const PrimaryButton = (
  link: string,
  text: string,
  css = '',
  target = '_blank',
  btntype = 'primary',
  ga: {
    eventCategory: string;
    eventAction: string;
    eventLabel: string;
  }
) => {
  const url = addExtraParamToUrl(link);

  btntype = btntype === 'No CTA' ? 'nocta' : btntype;

  return text !== '' ? (
    <a
      href={url}
      data-url={url}
      className={`${css} ${btntype.toLowerCase()}`}
      target={target}
      onClick={() => onClickGaTracking(ga)}
    >
      {text}
    </a>
  ) : (
    <></>
  );
};

/**
 *
 * @param link
 * @param image
 * @param target
 * @param alt
 * @param css
 * @returns
 */
const ImageButton = (
  link: string,
  image: string,
  css = '',
  target = '_blank',
  alt = '',
  ga: {
    eventCategory: string;
    eventAction: string;
    eventLabel: string;
  }
) => {
  const url = addExtraParamToUrl(link);
  return (
    <a href={url} className={`${css}`} target={target} onClick={() => onClickGaTracking(ga)}>
      <img src={image} alt={alt} className="lozad" />
    </a>
  );
};

/**
 *
 * @param imageSrc
 * @param blubText
 * @param url
 * @param target
 * @returns
 */
const ImageCard = (
  imageSrc: string,
  blubText: string,
  url: string,
  target = '',
  ga: {
    eventCategory: string;
    eventAction: string;
    eventLabel: string;
  },
  imageAltText = ''
) => {
  const urlWithParam = addExtraParamToUrl(url);
  const tinyImg = tinyImageForCards(imageSrc, ga.eventAction);
  let hasError = false;
  const onErrorImage = (e) => {
    hasError = true;
    e.target.src = 'https://mesocp3.azureedge.net/media/xmkl2qvu/default-image.png';
  };

  const onImageLoad = (e) => {
    if (!hasError) {
      imageLoad(e.currentTarget, imageSrc);
    }
  };

  return (
    <a
      {...(checkValue(urlWithParam) ? { href: urlWithParam } : {})}
      target={target}
      onClick={() => onClickGaTracking(ga)}
    >
      <div className="imgcard">
        <div className="img">
          <img
            src={tinyImg}
            data-placeholder-background="#e5e5e5"
            alt={imageAltText}
            data-text={blubText}
            className="lozad g-image-gallery"
            onLoad={(e) => {
              onImageLoad(e);
            }}
            onError={(e) => {
              onErrorImage(e);
            }}
            data-url={urlWithParam}
          />
        </div>
        <p className="imgdesc imglbl" dangerouslySetInnerHTML={{ __html: blubText }} />
      </div>
    </a>
  );
};
const ImageCardSingle = (
  imageSrc: string,
  blubText: string,
  url: string,
  target = '',
  ga: {
    eventCategory: string;
    eventAction: string;
    eventLabel: string;
  },
  imageAltText = ''
) => {
  const urlWithParam = addExtraParamToUrl(url);
  const tImage = tinyImageForSingleCard(imageSrc, ga.eventAction);

  return (
    <a href={urlWithParam} target={target} onClick={() => onClickGaTracking(ga)}>
      <div className="imgcard">
        <div className="img">
          <img
            src={tImage}
            data-placeholder-background="#e5e5e5"
            className="lozad"
            onLoad={(e) => {
              imageLoad(e.currentTarget, imageSrc);
            }}
            alt={imageAltText}
          />
        </div>
        {/* <div className="imgdesc imglbl" dangerouslySetInnerHTML={{ __html: blubText }} /> */}
        <p className="imgdesc imglbl" dangerouslySetInnerHTML={{ __html: blubText }} />
      </div>
    </a>
  );
};

/**
 *
 * @param link
 * @param socialTypeClass
 * @param socialText
 * @returns
 */
const SocialLink = (
  link: string,
  socialTypeClass: string,
  socialText: string,
  ga: {
    eventCategory: string;
    eventAction: string;
    eventLabel: string;
  }
) => {
  return (
    <a href={link} className={socialTypeClass} target="_blank" onClick={() => onClickGaTracking(ga)}>
      <div className={`s-icon ${socialTypeClass}`}></div>
      <span>{socialText}</span>
    </a>
  );
};

/**
 *
 * @param link
 * @param text
 * @param isPipActive
 * @returns
 */
const FooterLink = (
  link: string,
  text: string,
  isPipActive = false,
  ga: {
    eventCategory: string;
    eventAction: string;
    eventLabel: string;
  }
) => {
  return (
    <>
      <a href={link} data-url={link} target="_blank" onClick={() => onClickGaTracking(ga)}>
        {text}
      </a>
      {isPipActive && <span> |</span>}
    </>
  );
};

/**
 *
 * @param imageSrc
 * @param hName
 * @param rating
 * @param details
 * @param price
 * @param btnUrl
 * @param localizationText
 * @returns
 */
const HotelCard = (
  imageSrc: string,
  hName: string,
  rating: string,
  details: string,
  price: string,
  btnUrl: string,
  hotelCardCtaStyle: string,
  ga: {
    eventCategory: string;
    eventAction: string;
    eventLabel: string;
  },
  localizationText?: {
    btnText: string;
    fromText: string;
    enableFromText?: boolean;
    pricePerText: string;
  },
  imageAltText = '',
  guestRating?: string,
  footerMessage?: string,
  dateInfo?: string,
  fontClass?: string
) => {
  const htlUrl = btnUrl;

  const getRatings = () => {
    if (checkValue(guestRating)) {
      return (
        <>
          <div className="rating-infor-wrapper">
            <UitkRating rating={'1'} />&nbsp;<div className="details bodytxtsm">{guestRating}</div>
          </div>
        </>
      );
    } else {
      if (checkValue(rating)) {
        return (
          <>
            <UitkRating rating={rating} />
          </>
        );
      } else {
        return null;
      }
    }
  };

  return (
    <div className="hotel-card">
      <div className="hotel-card-wrapper">
        <div className="img-content">{ImageCard(`${imageSrc}`, '', htlUrl, '_blank', ga, imageAltText)}</div>
        <div className="details-content">
          <h4 className={ checkValue(fontClass) ? fontClass : '' }>{hName}</h4>
          <div className="rating">{getRatings()}</div>
          <div className="details bodytxtsm">{details}</div>
          {checkValue(footerMessage) && <div className="details bodytxtsm">{footerMessage}</div>}
          {checkValue(dateInfo) && <div className="details bodytxtsm">{dateInfo}</div>}
          <div className="hotel-action">
            <div className="price-wrapper">
              {checkValue(price) && (
                <>
                  {localizationText?.enableFromText && <div className="from bodytxtsm">{localizationText?.fromText}</div> }
                  <div className="price">
                    <span className="bold linktxt">{price}&nbsp;</span>
                    <span className="label bodytxtsm">{localizationText?.pricePerText}</span>
                  </div>
                </>
              )}
            </div>
            <div className="cta">
              {PrimaryButton(
                htlUrl,
                localizationText?.btnText,
                'btn htl-card-cta-btn btn-global',
                '_blank',
                hotelCardCtaStyle,
                ga
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 *
 * @param imageSrc
 * @param hName
 * @param rating
 * @param details
 * @param price
 * @param btnUrl
 * @param localizationText
 * @returns
 */
const HotelPartnerCard = (
  imageSrc: string,
  hName: string,
  hlocation: string,
  rating: string,
  details: string,
  price: string,
  strikeOut: string,
  btnUrl: string,
  ga: {
    eventCategory: string;
    eventAction: string;
    eventLabel: string;
  },
  localizationText?: {
    btnText: string;
    pricePerText: string;
    badge: string;
  },
  imageAltText?: string,
  partnerLogos?: {
    logo1: string;
    logo2: string;
  },
  imgCaption?: string,
  dateRange?: string
) => {
  const htlUrl = btnUrl;
  return (
    <div className="fh-partners-card">
      <div className="fh-partners-card-details">
        {checkValue(hName) && <Typo.HTMLText tag="h3">{hName}</Typo.HTMLText>}
        {checkValue(rating) && (
          <div className="fh-partners-card-details-rating">
            <UitkRating rating={rating} />
          </div>
        )}
        {checkValue(hlocation) && (
          <Typo.HTMLText exClass="strong" tag="p">
            {hlocation}
          </Typo.HTMLText>
        )}
        {checkValue(details) && <Typo.HTMLText tag="p">{details}</Typo.HTMLText>}
        {(checkValue(price) || checkValue(localizationText?.btnText)) && (
          <div className="fh-partners-card-details-action">
            {checkValue(price) && (
              <div>
                <p className="price">
                  {price}
                  {checkValue(strikeOut) && <span>{strikeOut}</span>}
                </p>
                <p className="price-txt">{localizationText?.pricePerText}</p>
                { checkValue(dateRange) && <p className="price-txt">{dateRange}</p> }
              </div>
            )}
            {PrimaryButton(htlUrl, localizationText?.btnText, 'btn btn-global', '_blank', 'primary', ga)}
          </div>
        )}
        {(checkValue(partnerLogos.logo1) || checkValue(partnerLogos.logo2)) && (
          <div className="fh-partners-card-details-logo">
            {partnerLogos.logo1 && (
              <img
                src={partnerLogos.logo1}
                data-placeholder-background="#e5e5e5"
                alt="partner logo 1"
                className="lozad"
              />
            )}
            {partnerLogos.logo1 && partnerLogos.logo2 && <div className="fh-partners-card-details-logo-divider"></div>}
            {partnerLogos.logo2 && (
              <img
                src={partnerLogos.logo2}
                data-placeholder-background="#e5e5e5"
                alt="partner logo 2"
                className="lozad"
              />
            )}
          </div>
        )}
      </div>
      <div className="fh-partners-card-img">
        {checkValue(localizationText?.badge) && (
          <span className="fh-partners-card-img-badge">{localizationText?.badge}</span>
        )}
        <figure>
          <img
            src={imageSrc}
            data-placeholder-background="#e5e5e5"
            alt={imageAltText}
            className="lozad"
            onLoad={(e) => {
              imageLoad(e.currentTarget, imageSrc);
            }}
          />
        </figure>
        {checkValue(imgCaption) && (
          <div className="fh-partners-card-img-caption">
            <UitkIcon name="error" size={UitkIconSize.EXTRA_SMALL} />
            {imgCaption}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 *
 * @param
 */
const ContentPanelCard = (
  imageSrc: string,
  heading: string,
  description: string,
  btnLink: string,
  btnText: string,
  target = '',
  btnType = 'primary',
  ga: {
    eventCategory: string;
    eventAction: string;
    eventLabel: string;
  },
  cpImageAltText: string
) => {
  const tImage = imageSrc.replace('width=685&height=495', 'width=7&height=5');

  return (
    <div className="content-panel-card">
      <div className="card-wrapper">
        <div className="order-style">
          <div className="description">
            <h2>{heading}</h2>
            <p className="bodytxtmd" dangerouslySetInnerHTML={{ __html: description }} />
            <div className="cta-action">
              {PrimaryButton(`${btnLink}`, btnText, 'btn btn-global g-content-panel-cta', target, btnType, ga)}
            </div>
          </div>
          <div className="img">
            <img
              src={tImage}
              data-placeholder-background="#e5e5e5"
              alt={cpImageAltText}
              className="lozad"
              onLoad={(e) => {
                imageLoad(e.currentTarget, imageSrc);
              }}
            />
          </div>
        </div>
        <div className="mobile">
          <div className="cta-action">
            {PrimaryButton(`${btnLink}`, btnText, 'btn btn-global', target, btnType, ga)}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 *
 * @param imageSrc
 * @param text
 * @param css
 * @returns
 */
const IconCard = (imageSrc: string, text: string, css = '') => {
  return (
    <div className={`icon-card ${css}`}>
      <img src={imageSrc} alt={text} />
      <p className="bodytxtreg">{text}</p>
    </div>
  );
};

/**
 * @param imageArray
 * @param device
 */
const getDeviceImage = (imageArray: any, device: string) => {
  let image = '';

  imageArray !== '' &&
    imageArray?.map((imgItem) => {
      if (imgItem.name.includes(device)) {
        image = imgItem.url;
      }
    });

  return image;
};

const useProgressiveImg = (lowQualitySrc, highQualitySrc) => {
  const [src, setSrc] = React.useState(lowQualitySrc);

  React.useEffect(() => {
    setSrc(lowQualitySrc);

    const img = new Image();
    img.src = encodeURI(highQualitySrc);

    img.onload = () => {
      setSrc(encodeURI(highQualitySrc));
    };
  }, [lowQualitySrc, highQualitySrc]);

  return [src, { blur: src === lowQualitySrc }];
};

const tinyImage = (image, device) => {
  if (device === 'desktop') return image.replace('width=2016&height=1092', 'width=37&height=17');
  if (device === 'tablet') return image.replace('width=768&height=500', 'width=26&height=17');
  if (device === 'mobile') return image.replace('width=375&height=500', 'width=15&height=20');
  return image;
};

const tinyImageForCards = (image, module) => {
  return module.indexOf('Hotel') > -1
    ? image.replace('_y.', '_e.')
    : image.replace('width=500&height=720', 'width=5&height=8');
};
const tinyImageForSingleCard = (image, module) => {
  return module.indexOf('Hotel') > -1
    ? image.replace('_y.', '_e.')
    : image.replace('width=640&height=390', 'width=7&height=5');
};

const tinyImageForProductCard = (image) => {
  return image.replace('width=640&height=390', 'width=7&height=5');
};

export {
  PrimaryButton,
  ImageButton,
  ImageCard,
  ImageCardSingle,
  SocialLink,
  FooterLink,
  HotelCard,
  HotelPartnerCard,
  ContentPanelCard,
  IconCard,
  getDeviceImage,
  useProgressiveImg,
  tinyImage,
  tinyImageForProductCard,
};
